import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient, setStoredTokens } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check; AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, space) => {
    const body = { email: (email || "").trim(), password: (password || "").trim() };
    if (space) body.space = space;
    const { data } = await apiClient.post("/auth/login", body);
    // MFA challenge: don't set user yet — caller handles OTP flow
    if (data?.mfa_required) return data;
    setUser(data);
    return data;
  };

  const refreshUser = checkAuth;

  const register = async (payload) => {
    const email = (payload.email || "").trim();
    const cleaned = { ...payload, email: email || null, password: (payload.password || "").trim() };
    const { data } = await apiClient.post("/auth/register", cleaned);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await apiClient.post("/auth/logout"); } catch {}
    // No state from the previous user may survive logout:
    // guest/legacy cart keys go away (the account cart stays under its own
    // scoped key for when the client logs back in) + volatile session data.
    try {
      localStorage.removeItem("neldaari_cart_guest");
      localStorage.removeItem("neldaari_cart_v1");
      sessionStorage.clear();
    } catch {}
    setStoredTokens(null); // jetons app mobile (Authorization: Bearer)
    setUser(null);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, refresh, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
